/**
 * RecordingModal — captures audio from the browser's microphone via
 * the MediaRecorder API, uploads to /api/field-tools/recordings, then
 * polls for transcription + AI summary.
 *
 * Props:
 *   open          — boolean
 *   onClose       — fn()
 *   customerId    — string (Customer.CustomerID)
 *   customerName  — string (for display + email subject)
 */
import { useEffect, useRef, useState } from "react";
import { Microphone, Stop, X, ArrowsClockwise, CheckCircle, Warning, PaperPlaneTilt, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

const POLL_INTERVAL_MS = 4000;
const POLL_MAX_TRIES = 60; // 4s * 60 = 4 minutes

export default function RecordingModal({ open, onClose, customerId, customerName = "", onSubmitted }) {
  const [phase, setPhase] = useState("idle"); // idle | recording | uploading | processing | done | error
  const [error, setError] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const [recId, setRecId] = useState("");
  const [doc, setDoc] = useState(null);

  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const tickRef = useRef(null);
  const startTsRef = useRef(0);
  const pollRef = useRef(null);

  // Reset state when closed/reopened.
  useEffect(() => {
    if (open) {
      setPhase("idle");
      setError("");
      setElapsed(0);
      setRecId("");
      setDoc(null);
    }
    return () => {
      stopEverything();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function stopEverything() {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      try { recorderRef.current.stop(); } catch { /* noop */ }
    }
    if (streamRef.current) {
      try { streamRef.current.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
      streamRef.current = null;
    }
  }

  async function startRecording() {
    setError("");
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      setError("Your browser doesn't support audio recording.");
      setPhase("error");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      // Choose the best supported mime — Chrome → audio/webm; Safari → audio/mp4.
      const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"];
      let mime = "";
      for (const m of candidates) {
        if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(m)) { mime = m; break; }
      }
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      recorderRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = onRecorderStop;
      rec.start();
      setPhase("recording");
      startTsRef.current = Date.now();
      tickRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startTsRef.current) / 1000));
      }, 250);
    } catch (e) {
      setError(e?.message || "Microphone permission denied.");
      setPhase("error");
    }
  }

  function stopRecording() {
    if (!recorderRef.current) return;
    try { recorderRef.current.stop(); } catch { /* noop */ }
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    if (streamRef.current) {
      try { streamRef.current.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
      streamRef.current = null;
    }
    setPhase("uploading");
  }

  async function onRecorderStop() {
    const blob = new Blob(chunksRef.current, { type: recorderRef.current?.mimeType || "audio/webm" });
    chunksRef.current = [];
    if (!blob.size) {
      setError("Recording was empty.");
      setPhase("error");
      return;
    }
    const ext = (recorderRef.current?.mimeType || "audio/webm").includes("mp4") ? "m4a"
              : (recorderRef.current?.mimeType || "audio/webm").includes("ogg") ? "ogg" : "webm";
    const filename = `recording-${customerId}-${Date.now()}.${ext}`;
    const finalElapsed = Math.floor((Date.now() - startTsRef.current) / 1000);

    const fd = new FormData();
    fd.append("customer_id", customerId);
    fd.append("customer_name", customerName);
    fd.append("duration_s", String(finalElapsed));
    fd.append("audio", blob, filename);
    try {
      const r = await api.post("/field-tools/recordings", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setRecId(r.data.id);
      setPhase("processing");
      try { onSubmitted?.(r.data.id); } catch { /* noop */ }
      // Begin polling for transcript + summary.
      let tries = 0;
      pollRef.current = setInterval(async () => {
        tries += 1;
        try {
          const rr = await api.get(`/field-tools/recordings/${r.data.id}`);
          const d = rr.data;
          setDoc(d);
          if (d.status === "completed") {
            clearInterval(pollRef.current); pollRef.current = null;
            setPhase("done");
            toast.success("Recording transcribed & summary emailed.");
          } else if (d.status?.startsWith("failed")) {
            clearInterval(pollRef.current); pollRef.current = null;
            setPhase("error");
            setError(d.status === "failed_transcribe"
              ? "Could not transcribe this recording. The audio may have been too short or unclear."
              : "Background processing failed.");
          } else if (tries >= POLL_MAX_TRIES) {
            clearInterval(pollRef.current); pollRef.current = null;
            setPhase("error");
            setError("Still processing — check the customer's recordings list in a few minutes.");
          }
        } catch (e) {
          // Transient — keep polling.
        }
      }, POLL_INTERVAL_MS);
    } catch (e) {
      setError(e?.response?.data?.detail || "Upload failed.");
      setPhase("error");
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[500] flex items-end sm:items-center justify-center bg-black/50 p-3 sm:p-6"
         data-testid="recording-modal" onClick={() => phase !== "recording" && phase !== "uploading" && phase !== "processing" && onClose?.()}>
      <div onClick={(e) => e.stopPropagation()}
           className="bg-white rounded-md shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <header className="flex items-center gap-2 border-b border-slate-200 px-4 py-3">
          <Microphone size={18} weight="fill" className="text-rose-600" />
          <h2 className="text-sm font-bold tracking-tight flex-1 truncate">
            Record conversation
            {customerName ? <span className="text-slate-500 font-normal"> · {customerName}</span> : null}
          </h2>
          <button onClick={() => onClose?.()} disabled={phase === "recording" || phase === "uploading"}
                  data-testid="recording-modal-close"
                  className="text-slate-400 hover:text-slate-700 disabled:opacity-40">
            <X size={16} weight="bold" />
          </button>
        </header>

        <div className="p-4 space-y-4">
          {phase === "idle" && (
            <>
              <p className="text-[12px] text-slate-600">
                Tap <strong>Start recording</strong> to capture the conversation. When you're done,
                we'll transcribe it with AI, summarize the key points, suggest next actions, and
                email the result to <strong>you</strong>.
              </p>
              <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2">
                <Warning size={11} weight="fill" className="inline mr-1" />
                Tell the customer you're recording — most jurisdictions require their consent before
                you press <em>Start</em>.
              </p>
              <button onClick={startRecording}
                      data-testid="recording-start"
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-sm bg-rose-600 hover:bg-rose-700 text-white text-sm font-bold uppercase tracking-wider">
                <Microphone size={16} weight="fill" /> Start recording
              </button>
            </>
          )}

          {phase === "recording" && (
            <>
              <div className="flex items-center justify-center gap-3 py-4">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-rose-600"></span>
                </span>
                <span className="text-2xl font-mono font-bold text-slate-900" data-testid="recording-elapsed">
                  {fmt(elapsed)}
                </span>
              </div>
              <p className="text-[12px] text-slate-600 text-center">
                Recording in progress — tap stop when finished.
              </p>
              <button onClick={stopRecording}
                      data-testid="recording-stop"
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-sm bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold uppercase tracking-wider">
                <Stop size={16} weight="fill" /> Stop & upload
              </button>
            </>
          )}

          {phase === "uploading" && (
            <div className="py-8 text-center">
              <ArrowsClockwise size={28} weight="bold" className="mx-auto animate-spin text-slate-400 mb-3" />
              <p className="text-sm text-slate-700">Uploading recording…</p>
            </div>
          )}

          {phase === "processing" && (
            <div className="py-6 text-center space-y-2">
              <Sparkle size={28} weight="fill" className="mx-auto text-blue-500 animate-pulse" />
              <p className="text-sm font-semibold text-slate-700">AI is transcribing &amp; summarizing…</p>
              <p className="text-[11px] text-slate-500">
                This usually takes 10–30 seconds per minute of audio.
                <br />You'll also get an email when it's done.
              </p>
              {doc?.status && (
                <p className="text-[10px] text-slate-400 uppercase tracking-wider mt-2">
                  Status: {doc.status}
                </p>
              )}
            </div>
          )}

          {phase === "done" && doc && (
            <div className="space-y-3" data-testid="recording-done">
              <div className="flex items-center gap-2 text-emerald-700">
                <CheckCircle size={18} weight="fill" />
                <span className="text-sm font-bold">Done · emailed to you</span>
              </div>
              {doc.summary && (
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Summary</div>
                  <p className="text-[13px] text-slate-800 bg-blue-50 border-l-4 border-blue-600 p-2">
                    {doc.summary}
                  </p>
                </div>
              )}
              {Array.isArray(doc.next_actions) && doc.next_actions.length > 0 && (
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Suggested next actions</div>
                  <ul className="text-[13px] text-slate-800 list-disc list-inside space-y-1">
                    {doc.next_actions.map((a, i) => (<li key={i}>{a}</li>))}
                  </ul>
                </div>
              )}
              {doc.transcript && (
                <details>
                  <summary className="cursor-pointer text-[11px] font-bold uppercase tracking-wider text-slate-600">
                    View full transcript
                  </summary>
                  <p className="mt-2 text-[12px] text-slate-700 bg-slate-50 border border-slate-200 p-2 whitespace-pre-wrap">
                    {doc.transcript}
                  </p>
                </details>
              )}
              <button onClick={() => onClose?.()}
                      data-testid="recording-done-close"
                      className="w-full inline-flex items-center justify-center gap-1 px-3 py-2 rounded-sm bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold uppercase tracking-wider">
                <PaperPlaneTilt size={12} weight="fill" /> Done
              </button>
            </div>
          )}

          {phase === "error" && (
            <div className="space-y-3" data-testid="recording-error">
              <div className="flex items-start gap-2 text-rose-700 bg-rose-50 border border-rose-200 rounded-sm p-3">
                <Warning size={16} weight="fill" className="mt-0.5 flex-shrink-0" />
                <p className="text-sm">{error || "Something went wrong."}</p>
              </div>
              <button onClick={() => { setPhase("idle"); setError(""); }}
                      className="w-full inline-flex items-center justify-center px-3 py-2 rounded-sm border border-slate-300 text-slate-700 hover:bg-slate-50 text-xs font-bold uppercase tracking-wider">
                Try again
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function fmt(s) {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}
